import { type Primitive } from '../types/common';
import { isDate } from '../utils/guards';
import { get } from '../utils/object';

/**
 * Generic sorter function
 * @param a First value
 * @param b Second value
 * @param options Sorter options
 * @returns Comparison result (-1, 0, 1)
 */
export const sorter = <T extends Primitive | Date>(a: T, b: T, options: SorterOptions): number => {
	let comparison = 0;
	if (typeof a === 'string' && typeof b === 'string') comparison = a.localeCompare(b);
	else if (isDate(a) && isDate(b)) comparison = a.getTime() - b.getTime();
	else if (typeof a === 'bigint' && typeof b === 'bigint') comparison = Number(a) - Number(b);
	else comparison = (a as number) - (b as number);

	return options.direction === 'asc' ? comparison : -comparison;
};

/**
 * Generic array of objects sorter
 * @author filipditrich <ditrich@nfctron.com>
 * @param prop Property to sort by
 * @param options Sorter options
 * @returns Sorter function
 */
export const sortBy =
	<T extends Record<PropertyKey, any>, P extends keyof T = keyof T>(prop: P | ((value: T) => any), options: SortByOptions<T, P> = {}) =>
	(a: T, b: T): number => {
		/** prop is a getter */
		if (typeof prop === 'function') return sorter(prop(a), prop(b), options);
		/** prop is a path */
		const getter = options.getValue || ((value: any) => value);
		const aProp = get(a, prop);
		const bProp = get(b, prop);
		return sorter(getter(aProp), getter(bProp), options);
	};

/**
 * Options for sortBy function
 * @export
 */
export type SortByOptions<T extends Record<PropertyKey, any>, P extends keyof T = keyof T> = SorterOptions & {
	getValue?: (value: T[P]) => any;
};

/**
 * Options for sortBy function
 * @export
 */
export type SorterOptions = {
	direction?: 'asc' | 'desc' | null;
};
